import { useEffect, useRef, useState } from 'react';
import { useRunStore } from '../store/runStore';
import { useUserSettings } from '../userSettings';

const WIPE_DURATION_MS = 360;
let _tid = 0;

export function RoomTransition() {
  const roomIndex = useRunStore((s) => s.currentRoomIndex);
  const phase = useRunStore((s) => s.phase);
  const userSettings = useUserSettings();
  const [tickId, setTickId] = useState<number | null>(null);
  const lastRoomRef = useRef<number>(roomIndex);
  const lastPhaseRef = useRef<string>(phase);

  useEffect(() => {
    if (userSettings.reducedMotion) {
      lastRoomRef.current = roomIndex;
      lastPhaseRef.current = phase;
      return;
    }

    const prevRoom = lastRoomRef.current;
    const prevPhase = lastPhaseRef.current;
    lastRoomRef.current = roomIndex;
    lastPhaseRef.current = phase;

    // Fire on actual room advance (currentRoomIndex changed) AND we're entering combat.
    // Skip on initial mount (when prevPhase was 'menu') and on phase transitions
    // unrelated to room advance.
    const isAdvance = roomIndex !== prevRoom && prevPhase !== 'menu' && phase === 'in_combat';
    if (!isAdvance) return;

    const id = ++_tid;
    setTickId(id);
    const t = setTimeout(() => {
      setTickId((current) => (current === id ? null : current));
    }, WIPE_DURATION_MS);
    return () => { clearTimeout(t); };
  }, [roomIndex, phase, userSettings.reducedMotion]);

  if (tickId === null) return null;

  return (
    <div
      key={tickId}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 14, // above HUD, below WaveClearBanner+modals
        overflow: 'hidden',
      }}
    >
      {/* Wipe band: a horizontal stripe that sweeps from top to bottom */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: '40vh',
          background: 'linear-gradient(180deg, transparent 0%, rgba(0, 255, 255, 0.18) 30%, rgba(255, 0, 255, 0.28) 50%, rgba(0, 255, 255, 0.18) 70%, transparent 100%)',
          animation: `room-wipe ${WIPE_DURATION_MS}ms linear forwards`,
        }}
      />
      {/* Scan stripes that briefly intensify */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(0, 255, 255, 0.08) 4px, rgba(0, 255, 255, 0.08) 5px)',
          animation: `wipe-flash ${WIPE_DURATION_MS}ms linear forwards`,
        }}
      />
    </div>
  );
}
